import React, { useState } from 'react';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { 
  ArrowRight, 
  ChevronRight,
  Circle
} from 'lucide-react';

// Define the lecture types
interface Lecture {
  id: string;
  title: string;
  description: string;
  path: string;
  category: string;
  tags: string[];
  duration?: string;
  level?: 'Foundational' | 'Intermediate' | 'Advanced';
}

const MentorshipContent: React.FC = () => {
  const [hoveredLecture, setHoveredLecture] = useState<string | null>(null);
  
  // Define the lectures data with enhanced metadata
  const lectures: Lecture[] = [

    {
      id: 'semaglutide-mechanism-precision-update',
      title: 'Semaglutide\'s Mechanism: A Precision Update',
      description: 'From regional understanding to cellular precision: How new research transforms our approach to GLP-1 therapy with actionable clinical insights.',
      path: '/mentorship/lectures/semaglutide-mechanism-precision-update',
      category: 'Longevity Updates',
      tags: ['Semaglutide', 'Adcyap1+ Neurons', 'Precision Medicine', 'Neural Circuits', 'Fat Loss', 'Clinical Practice']
    },
    {
      id: 'glp1s-and-longevity-atria-v3',
      title: 'GLP-1s and Longevity Atria',
      description: 'Advanced clinical perspectives on GLP-1 agonists for healthspan optimization with comprehensive mechanistic insights.',
      path: '/mentorship/lectures/glp1s-and-longevity-atria-v3',
      category: 'Longevity Updates',
      tags: ['GLP-1', 'Scientific Visualization', 'Mechanisms', 'Multi-Agonist', 'Clinical Evidence', 'Advanced Science']
    },
    {
      id: 'lecture-generator',
      title: 'Lecture Generator',
      description: 'Generate consistent lecture components using the inflammation lecture template. Input your content and get a fully formatted lecture component. (Restricted access)',
      path: '/mentorship/lecture-generator',
      category: 'Tools',
      tags: ['Development', 'Templates', 'Automation', 'Content Creation']
    },
    {
      id: 'evaluating-business-opportunities',
      title: 'Evaluating Business Opportunities',
      description: 'How to assess requests and structure partnerships that respect your time.',
      path: '/mentorship/lectures/evaluating-business-opportunities',
      category: 'Business',
      tags: ['Entrepreneurship', 'Strategy', 'Time Management']
    },
    {
      id: 'shift-framework-health-longevity',
      title: 'The SHIFT Framework for Health and Longevity',
      description: 'A visual guide to the five domains that govern cellular aging with clinical applications.',
      path: '/mentorship/lectures/shift-framework-health-longevity',
      category: 'Cellular Foundations',
      tags: ['Cellular Biology', 'Aging', 'Visual Learning']
    },
    {
      id: 'cortisol-molecular-foundations',
      title: 'Cortisol: Molecular Foundations',
      description: 'Explore the cellular mechanisms, clinical significance, and therapeutic applications of cortisol in longevity medicine.',
      path: '/mentorship/lectures/cortisol-molecular-foundations',
      category: 'Cellular Foundations',
      tags: ['Hormones', 'Stress', 'Molecular Medicine']
    },
    {
      id: 'questioning-gold-standard',
      title: 'Questioning the Gold Standard',
      description: 'Why Randomized Controlled Trials and Biostatistics Fail to Guide Clinical Practice',
      path: '/mentorship/lectures/questioning-gold-standard',
      category: 'Clinical Applications',
      tags: ['Research', 'Evidence', 'Clinical Methodology']
    },
    {
      id: 'cellular-basis-health-disease',
      title: 'Cellular Basis of Health and Disease',
      description: 'A comprehensive primer on longevity medicine fundamentals for physicians, covering cellular aging mechanisms and therapeutic interventions.',
      path: '/mentorship/lectures/cellular-basis-health-disease',
      category: 'Cellular Foundations',
      tags: ['Cellular Biology', 'Aging', 'Fundamentals', 'Signaling Pathways']
    },

    {
      id: 'what-is-longevity-medicine',
      title: 'What Is Longevity Medicine?',
      description: 'An introduction to the principles, scope, and practice of longevity-focused clinical care.',
      path: '/mentorship/lectures/what-is-longevity-medicine',
      category: 'Clinical Applications',
      tags: ['Introduction', 'Principles', 'Clinical Practice']
    },
    {
      id: 'mitochondrial-health',
      title: 'Mitochondrial Health',
      description: 'In-depth exploration of mitochondrial function, dysfunction, and therapeutic interventions for longevity.',
      path: '/mentorship/lectures/mitochondrial-health',
      category: 'Cellular Foundations',
      tags: ['Energy', 'Metabolism', 'Aging']
    },
    {
      id: 'epigenetics-cellular-medicine',
      title: 'Epigenetics & Cellular Medicine',
      description: 'How epigenetic modifications influence aging and how to target them clinically.',
      path: '/mentorship/lectures/epigenetics-cellular-medicine',
      category: 'Cellular Foundations',
      tags: ['Genetics', 'Aging', 'Regulation']
    },
    {
      id: 'peroxisomes-health-disease',
      title: 'Peroxisomes in Health & Disease',
      description: 'Clinical relevance of peroxisome function and dysfunction in aging and metabolic health.',
      path: '/mentorship/lectures/peroxisomes-health-disease',
      category: 'Cellular Foundations',
      tags: ['Metabolism', 'Toxins', 'Cellular Function']
    },
    {
      id: 'hormones-cellular-fundamentals',
      title: 'Hormones: Cellular Fundamentals',
      description: 'Comprehensive overview of hormone systems from a cellular medicine perspective.',
      path: '/mentorship/lectures/hormones-cellular-fundamentals',
      category: 'Cellular Foundations',
      tags: ['Endocrinology', 'Cellular Signaling']
    },
    {
      id: 'testosterone-molecular-foundations',
      title: 'Testosterone: Molecular Foundations',
      description: 'Detailed exploration of testosterone biochemistry, signaling pathways, and physiological effects.',
      path: '/mentorship/lectures/testosterone-molecular-foundations',
      category: 'Cellular Foundations',
      tags: ['Men\'s Health', 'Endocrinology']
    },
    {
      id: 'testosterone-clinical-applications',
      title: 'Testosterone: Clinical Applications',
      description: 'Practical guide to testosterone replacement therapy, monitoring, and optimization protocols.',
      path: '/mentorship/lectures/testosterone-clinical-applications',
      category: 'Clinical Applications',
      tags: ['Men\'s Health', 'HRT', 'Protocols']
    },
    {
      id: 'estrogen-molecular-foundations',
      title: 'Estrogen: Molecular Foundations',
      description: 'Comprehensive analysis of estrogen biochemistry, receptor activity, and physiological functions.',
      path: '/mentorship/lectures/estrogen-molecular-foundations',
      category: 'Cellular Foundations',
      tags: ['Women\'s Health', 'Endocrinology']
    },
    {
      id: 'estrogen-clinical-applications',
      title: 'Estrogen: Clinical Applications',
      description: 'Evidence-based approaches to estrogen replacement therapy and management.',
      path: '/mentorship/lectures/estrogen-clinical-applications',
      category: 'Clinical Applications',
      tags: ['Women\'s Health', 'HRT', 'Protocols']
    },
    {
      id: 'thyroid-molecular-clinical',
      title: 'Thyroid: Molecular & Clinical',
      description: 'Integrated approach to thyroid health from molecular mechanisms to clinical management.',
      path: '/mentorship/lectures/thyroid-molecular-clinical',
      category: 'Clinical Applications',
      tags: ['Metabolism', 'Endocrinology', 'Protocols']
    },
    {
      id: 'ehr-platforms-for-longevity',
      title: 'EHR Platforms for Longevity',
      description: 'Evaluation of electronic health record systems optimized for longevity medicine practices.',
      path: '/mentorship/lectures/ehr-platforms-for-longevity',
      category: 'Business',
      tags: ['Software', 'Clinic Operations', 'Practice Management']
    },
    {
      id: 'glp1-agonists',
      title: 'GLP-1 Receptor Agonists',
      description: 'Advanced clinical applications of GLP-1 agonists beyond diabetes management.',
      path: '/mentorship/lectures/glp1-agonists',
      category: 'Clinical Applications',
      tags: ['Metabolism', 'Weight Management', 'Pharmacology']
    },
    {
      id: 'inflammation',
      title: 'Inflammation & Inflammaging',
      description: 'Mechanisms, biomarkers, and clinical strategies for managing chronic inflammation in aging.',
      path: '/mentorship/lectures/inflammation',
      category: 'Clinical Applications',
      tags: ['Aging', 'Immune System', 'Biomarkers']
    },
    {
      id: 'nac-clinical-application',
      title: 'N-Acetylcysteine (NAC) Clinical Applications',
      description: 'Mechanisms, dosing strategies, and therapeutic applications of NAC in longevity medicine and chronic disease prevention.',
      path: '/mentorship/lectures/nac-clinical-application',
      category: 'Clinical Applications',
      tags: ['Antioxidants', 'Detoxification', 'Glutathione']
    },
    {
      id: 'anatomy-of-a-longevity-consult',
      title: 'Anatomy of a Longevity Consult',
      description: 'Step-by-step overview of an initial longevity consultation from intake to treatment planning.',
      path: '/mentorship/lectures/anatomy-of-a-longevity-consult',
      category: 'Clinical Applications',
      tags: ['Consultation', 'Practice Management']
    },
    {
      id: 'womens-longevity-interventions',
      title: 'The Top 5 Most Powerful Longevity Interventions for Women',
      description: 'A comprehensive SHIFT Framework approach to women\'s health in midlife, targeting cellular signaling pathways for optimal longevity outcomes. Covers resistance training, plant-predominant diet with FGF21 activation, microdosed GLP-1 agonists, hormone replacement therapy, and testosterone optimization.',
      path: '/mentorship/lectures/womens-longevity-interventions',
      category: 'Clinical Applications',
      tags: ['Women\'s Health', 'SHIFT Framework', 'Hormone Optimization', 'Longevity Medicine', 'Midlife Health']
    },









    {
      id: 'supplements-nutraceuticals',
      title: 'Supplements and Nutraceuticals in Longevity Medicine: Evidence-Based Clinical Application',
      description: 'Comprehensive clinical guide to evidence-based supplementation and nutraceutical protocols for longevity medicine. Covers diagnostic pillars, biomarker assessment, NAD+ precursors, senolytics, omega-3s, vitamin D, and the complete SHIFT framework approach with 34 detailed slides including clinical protocols and safety considerations.',
      path: '/mentorship/lectures/supplements-nutraceuticals',
      category: 'Clinical Applications',
      tags: ['Supplements', 'Nutraceuticals', 'Clinical Protocols', 'Biomarkers', 'Evidence-Based Medicine', 'SHIFT Framework']
    },
    {
      id: 'peptides-shift-approach',
      title: 'Peptides: A SHIFT Approach',
      description: 'Therapeutic peptides in longevity medicine organized by the SHIFT framework. Covers GLP-1 receptor agonists, growth hormone secretagogues, thymosin alpha-1, BPC-157, thymosin beta-4, epitalon, and MOTS-c. Features comprehensive mechanisms, clinical protocols, regulatory considerations, and safety profiles.',
      path: '/mentorship/lectures/peptides-shift-approach',
      category: 'Clinical Applications',
      tags: ['Peptide Therapy', 'SHIFT Framework', 'Clinical Protocols', 'Longevity Medicine', 'Regulatory Considerations', 'Safety Profiles']
    },
    {
      id: 'taking-investment-selling-practice',
      title: 'Taking investment and selling your practice [Guest Lecture]',
      description: 'Expert insights on practice valuation, investment strategies, and exit planning for longevity medicine practices. Features industry experts sharing real-world insights and practical strategies for practice owners considering investment or exit opportunities.',
      path: '/mentorship/lectures/taking-investment-selling-practice',
      category: 'Business',
      tags: ['Practice Valuation', 'Investment Strategy', 'Exit Planning', 'Business Development', 'Guest Lecture', 'Financial Strategy']
    },
    {
      id: 'cardiovascular-disease-longevity',
      title: 'Cardiovascular Disease in the Age of Longevity',
      description: 'A clinician\'s guide to health optimization and extended healthspan through advanced cardiovascular medicine. Covers systems-biology approaches, pathophysiological pillars, signaling pathways, and proactive optimization strategies beyond traditional disease management.',
      path: '/mentorship/lectures/cardiovascular-disease-longevity',
      category: 'Clinical Applications',
      tags: ['Cardiovascular Health', 'Longevity Medicine', 'Systems Biology', 'Health Optimization']
    },
    {
      id: 'exosomes',
      title: 'Exosomes in Longevity, Functional Medicine, and Health Optimization',
      description: 'Nature\'s nanomessengers for clinicians. Comprehensive exploration of exosome biology, mechanisms, and clinical potential in longevity medicine. Covers biogenesis, cargo composition, intercellular communication, and therapeutic applications.',
      path: '/mentorship/lectures/exosomes',
      category: 'Clinical Applications',
      tags: ['Regenerative Medicine', 'Cell Therapy', 'Nanomedicine', 'Therapeutic Applications']
    },

    {
      id: 'long-covid',
      title: 'Long COVID: A Longevity, Functional Medicine, and Health Optimization Perspective',
      description: 'Comprehensive exploration of Post-Acute Sequelae of SARS-CoV-2 infection (PASC) through longevity science and functional medicine. Covers pathophysiological mechanisms, core signaling pathways, immune dysregulation, and evidence-based therapeutic strategies.',
      path: '/mentorship/lectures/long-covid',
      category: 'Clinical Applications',
      tags: ['Long COVID', 'PASC', 'Immune Dysfunction', 'Chronic Inflammation', 'Functional Medicine']
    },
    {
      id: 'neurodegenerative-disease',
      title: 'Neurodegenerative Disease: A Longevity and Functional Medicine Approach',
      description: 'Comprehensive approach to health optimization in neurodegenerative diseases. Covers key mechanisms including cellular senescence, neuroinflammation, autophagy, mitochondrial pathways, and personalized interventions. Integrates prevention, biomarkers, and therapeutic protocols.',
      path: '/mentorship/lectures/neurodegenerative-disease',
      category: 'Clinical Applications',
      tags: ['Neurodegeneration', 'Alzheimer\'s', 'Parkinson\'s', 'Cellular Senescence', 'Neuroprotection']
    },
    {
      id: 'advanced-therapeutic-innovations',
      title: 'Advanced Therapeutic Innovations: Peptides, Hormones, & Regenerative Medicine',
      description: 'Comprehensive exploration of cutting-edge therapeutic modalities in longevity medicine. Covers peptide therapeutics, hormonal optimization, and regenerative medicine including stem cells and exosomes. Features detailed mechanisms, clinical applications, and personalized treatment strategies.',
      path: '/mentorship/lectures/advanced-therapeutic-innovations',
      category: 'Clinical Applications',
      tags: ['Peptide Therapy', 'Hormonal Optimization', 'Regenerative Medicine', 'Stem Cells', 'Exosomes']
    },
    {
      id: 'brain-health-neurofeedback',
      title: 'Brain Health: Neurofeedback, Quantitative EEG, Electroceuticals',
      description: 'Advanced neurotechnologies for brain optimization and cognitive enhancement. Covers quantitative EEG brain mapping, neurofeedback training protocols, and electroceutical interventions. Explores neuro-longevity strategies, neuroplasticity enhancement, and personalized brain health optimization.',
      path: '/mentorship/lectures/brain-health-neurofeedback',
      category: 'Clinical Applications',
      tags: ['Neurofeedback', 'qEEG', 'Electroceuticals', 'Brain Optimization', 'Neuroplasticity']
    },
    {
      id: 'advanced-diagnostics',
      title: 'Advanced Diagnostics: Epigenetics, Metabolomics, Microbiome Analysis, & Proteomics',
      description: 'Comprehensive exploration of cutting-edge diagnostic modalities transforming longevity medicine and personalized health optimization. Covers epigenetic clocks, metabolomic signatures, microbiome analysis, and proteomic biomarkers with detailed mechanisms and clinical applications.',
      path: '/mentorship/lectures/advanced-diagnostics',
      category: 'Clinical Applications',
      tags: ['Epigenetics', 'Metabolomics', 'Microbiome', 'Proteomics', 'Precision Medicine']
    },
    {
      id: 'functional-advanced-lab-testing',
      title: 'Functional and Advanced Lab Testing for Longevity and Health Optimization',
      description: 'A clinician\'s introduction to proactive, personalized medicine. Covers the paradigm shift from reactive to proactive medicine, key pillars of longevity assessment including redox balance, autophagy, immunosenescence, mitochondrial function, and nutrient-sensing pathways with detailed biomarkers and interventions.',
      path: '/mentorship/lectures/functional-advanced-lab-testing',
      category: 'Clinical Applications',
      tags: ['Lab Testing', 'Biomarkers', 'Functional Medicine', 'Proactive Medicine', 'Longevity Assessment']
    },
    {
      id: 'lifestyle-interventions',
      title: 'Lifestyle Interventions for Longevity & Health Optimization',
      description: 'Comprehensive clinical guide to evidence-based lifestyle interventions targeting the core mechanisms of aging and health optimization. Covers nutrition science, exercise physiology, movement protocols, and sleep optimization with detailed molecular mechanisms and practical implementation strategies.',
      path: '/mentorship/lectures/lifestyle-interventions',
      category: 'Clinical Applications',
      tags: ['Nutrition Science', 'Exercise Physiology', 'Sleep Optimization', 'Lifestyle Medicine', 'Health Optimization']
    },
    {
      id: 'pharmaceuticals-repurposed-medications',
      title: 'Pharmaceuticals and Repurposed Medications for Longevity: A Functional Medicine Approach',
      description: 'Comprehensive exploration of evidence-based pharmaceutical interventions for longevity including mTOR modulators (rapamycin), AMPK activators (metformin), senolytics, NAD+ precursors, and clinical implementation strategies. Features detailed mechanisms, biomarkers, dosing protocols, and safety considerations.',
      path: '/mentorship/lectures/pharmaceuticals-repurposed-medications',
      category: 'Clinical Applications',
      tags: ['Pharmaceuticals', 'Drug Repurposing', 'mTOR', 'AMPK', 'Senolytics', 'NAD+', 'Clinical Implementation']
    },
    {
      id: 'microdosing-glp1s-2',
      title: 'Microdosing GLP-1s 2.0: Advanced Strategies Through the SHIFT Framework',
      description: 'Advanced GLP-1 receptor agonist strategies for longevity medicine practitioners. Deep dive into next-generation compounds like retatrutide, comprehensive SHIFT framework application, clinical implementation protocols, and cutting-edge research on biological age reversal.',
      path: '/mentorship/lectures/microdosing-glp1s-2',
      category: 'Clinical Applications',
      tags: ['GLP-1 2.0', 'Retatrutide', 'SHIFT Framework', 'Longevity Medicine', 'Advanced Protocols', 'Age Reversal']
    },
    {
      id: 'measuring-senescence-clinically',
      title: 'Measuring Senescence Clinically',
      description: 'Comprehensive guide to clinical assessment of cellular senescence including biomarkers, imaging techniques, and practical implementation strategies for longevity medicine practice.',
      path: '/mentorship/lectures/measuring-senescence-clinically',
      category: 'Longevity Updates',
      tags: ['Senescence', 'Biomarkers', 'Clinical Assessment', 'Aging', 'Diagnostics']
    },
  ];

  // Separate business and clinical/scientific lectures
  const businessLectures = lectures.filter(l => l.category === 'Business');
  const clinicalLectures = lectures.filter(l => l.category === 'Clinical Applications');
  const foundationalLectures = lectures.filter(l => l.category === 'Cellular Foundations');
  const longevityUpdatesLectures = lectures.filter(l => l.category === 'Longevity Updates');
  
  // Core Curriculum lectures - the essential learning path
  const coreCurriculumIds = [
    'advanced-diagnostics',
    'shift-framework-health-longevity',
    'advanced-therapeutic-innovations',
    'brain-health-neurofeedback',
    'cardiovascular-disease-longevity',
    'cellular-basis-health-disease',
    'exosomes',
    'functional-advanced-lab-testing',
    'lifestyle-interventions',
    'long-covid',
    'neurodegenerative-disease',
    'pharmaceuticals-repurposed-medications',
    'microdosing-glp1s-2',
    'supplements-nutraceuticals'
  ];
  
  const coreCurriculumLectures = lectures.filter(l => coreCurriculumIds.includes(l.id));
  
  // List-based lecture item component
  const LectureItem = ({ lecture, index }: { lecture: Lecture; index: number }) => (
    <Link href={lecture.path}>
      <div 
        className="group py-4 transition-all duration-200"
        onMouseEnter={() => setHoveredLecture(lecture.id)}
        onMouseLeave={() => setHoveredLecture(null)}
      >
        <div className="flex items-start gap-6">
          {/* Index number */}
          <div className="flex-shrink-0 w-8 pt-0.5">
            <span className="text-xs text-gray-400 font-mono">
              {String(index + 1).padStart(2, '0')}
            </span>
          </div>
          
          {/* Content */}
          <div className="flex-grow">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-grow">
                <h3 className="text-base font-medium text-gray-900 mb-1 group-hover:text-indigo-600 transition-colors">
                  {lecture.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-2 max-w-2xl">
                  {lecture.description}
                </p>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span className="uppercase tracking-wider">{lecture.category}</span>
                  {lecture.level && (
                    <>
                      <Circle className="w-1 h-1 fill-current" />
                      <span>{lecture.level}</span>
                    </>
                  )}
                  {lecture.tags.length > 0 && (
                    <>
                      <Circle className="w-1 h-1 fill-current" />
                      <span>{lecture.tags.slice(0, 2).join(', ')}</span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Arrow indicator */}
              <div className="flex-shrink-0 pt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowRight className="h-4 w-4 text-indigo-600" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Subtle separator */}
        <div className="mt-4 ml-14">
          <Separator className="bg-gray-100" />
        </div>
      </div>
    </Link>
  );

  // Category section with list layout
  const CategorySection = ({ 
    title, 
    lectures,
    startIndex = 0
  }: { 
    title: string; 
    lectures: Lecture[];
    startIndex?: number;
  }) => (
    <div className="mb-12">
      <div className="mb-6">
        <h2 className="text-xl font-medium text-gray-900">{title}</h2>
        <p className="text-xs text-gray-500 mt-1">
          {lectures.length} {lectures.length === 1 ? 'lecture' : 'lectures'}
        </p>
      </div>
      
      <div>
        {lectures.map((lecture, idx) => (
          <LectureItem 
            key={lecture.id} 
            lecture={lecture} 
            index={startIndex + idx}
          />
        ))}
      </div>
    </div>
  );

  // Compact list item for all content view
  const CompactLectureItem = ({ lecture }: { lecture: Lecture }) => (
    <Link href={lecture.path}>
      <div 
        className="group py-2 px-3 -mx-3 rounded-sm hover:bg-gray-50 transition-colors"
        onMouseEnter={() => setHoveredLecture(lecture.id)}
        onMouseLeave={() => setHoveredLecture(null)}
      >
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900 group-hover:text-indigo-600 transition-colors">
              {lecture.title}
            </h4>
            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
              <span>{lecture.category}</span>
              {lecture.level && (
                <>
                  <Circle className="w-1 h-1 fill-current" />
                  <span>{lecture.level}</span>
                </>
              )}
            </div>
          </div>
          <ChevronRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
        </div>
      </div>
    </Link>
  );

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Navbar />
      <main className="flex-grow">
        {/* Minimal Hero Section */}
        <section className="bg-white border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-6 py-12">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 mb-6">
                <Link href="/mentorship" className="hover:text-gray-700 transition-colors">
                  Mentorship
                </Link>
                <span>/</span>
                <span className="text-gray-700">Curriculum</span>
              </div>
              
              <h1 className="text-3xl font-light text-gray-900 mb-4 tracking-tight">
                Mentorship Curriculum
              </h1>
              
              <p className="text-base text-gray-600 leading-relaxed mb-8 max-w-2xl">
                A comprehensive lecture series designed to advance your knowledge in longevity medicine, from molecular foundations to practice management.
              </p>
              
              <div className="flex items-center gap-6 text-sm text-gray-500">
                <span>{lectures.length} lectures</span>
                <Circle className="w-1 h-1 fill-current" />
                <span>40+ hours</span>
                <Circle className="w-1 h-1 fill-current" />
                <span>Expert-led</span>
              </div>
            </div>
          </div>
        </section>

        {/* Content Navigation */}
        <section className="sticky top-0 z-40 bg-white border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-6">
            <Tabs defaultValue="organized" className="w-full">
              <TabsList className="h-auto p-0 bg-transparent rounded-none border-0 flex items-center justify-start">
                <TabsTrigger 
                  value="organized" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-gray-900 data-[state=active]:bg-transparent px-0 mr-8 py-4 text-sm font-medium text-gray-600 data-[state=active]:text-gray-900 hover:text-gray-900 transition-colors"
                >
                  By Category
                </TabsTrigger>
                <TabsTrigger 
                  value="core-curriculum" 
                  className="relative rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-600 data-[state=active]:bg-transparent px-0 mr-8 py-4 text-sm font-medium text-gray-600 data-[state=active]:text-indigo-600 hover:text-gray-900 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span>Core Curriculum</span>
                    <Badge 
                      variant="secondary" 
                      className="text-[10px] px-1.5 py-0 h-4 bg-indigo-100 text-indigo-700 border-0 font-medium"
                    >
                      START HERE
                    </Badge>
                  </div>
                </TabsTrigger>
                <TabsTrigger 
                  value="tools" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-gray-900 data-[state=active]:bg-transparent px-0 mr-8 py-4 text-sm font-medium text-gray-600 data-[state=active]:text-gray-900 hover:text-gray-900 transition-colors"
                >
                  Tools
                </TabsTrigger>
                <TabsTrigger 
                  value="all" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-gray-900 data-[state=active]:bg-transparent px-0 py-4 text-sm font-medium text-gray-600 data-[state=active]:text-gray-900 hover:text-gray-900 transition-colors"
                >
                  All Lectures
                </TabsTrigger>
              </TabsList>

              {/* Organized by Category */}
              <TabsContent value="organized" className="mt-0">
                                  <div className="py-8">
                    {/* Category Sub-tabs */}
                    <Tabs defaultValue="longevity" className="w-full">
                      <TabsList className="h-auto p-1 bg-gray-50 rounded-lg mb-8 inline-flex">
                        <TabsTrigger 
                          value="longevity" 
                          className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs px-4 py-2.5 rounded-md transition-all"
                        >
                          <span className="flex items-center gap-2">
                            Longevity Updates
                            <span className="text-[10px] font-medium text-gray-500 data-[state=active]:text-gray-700">
                              {longevityUpdatesLectures.length}
                            </span>
                          </span>
                        </TabsTrigger>
                        <TabsTrigger 
                          value="clinical" 
                          className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs px-4 py-2.5 rounded-md transition-all"
                        >
                          <span className="flex items-center gap-2">
                            Clinical Applications
                            <span className="text-[10px] font-medium text-gray-500 data-[state=active]:text-gray-700">
                              {clinicalLectures.length}
                            </span>
                          </span>
                        </TabsTrigger>
                        <TabsTrigger 
                          value="foundations" 
                          className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs px-4 py-2.5 rounded-md transition-all"
                        >
                          <span className="flex items-center gap-2">
                            Cellular Foundations
                            <span className="text-[10px] font-medium text-gray-500 data-[state=active]:text-gray-700">
                              {foundationalLectures.length}
                            </span>
                          </span>
                        </TabsTrigger>
                        <TabsTrigger 
                          value="business" 
                          className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs px-4 py-2.5 rounded-md transition-all"
                        >
                          <span className="flex items-center gap-2">
                            Business Topics
                            <span className="text-[10px] font-medium text-gray-500 data-[state=active]:text-gray-700">
                              {businessLectures.length}
                            </span>
                          </span>
                        </TabsTrigger>
                      </TabsList>

                    <TabsContent value="longevity" className="mt-0">
                      <CategorySection
                        title="Longevity Updates"
                        lectures={longevityUpdatesLectures.reverse()}
                        startIndex={0}
                      />
                    </TabsContent>

                    <TabsContent value="clinical" className="mt-0">
                      <CategorySection
                        title="Clinical Applications"
                        lectures={clinicalLectures}
                        startIndex={0}
                      />
                    </TabsContent>

                    <TabsContent value="foundations" className="mt-0">
                      <CategorySection
                        title="Cellular & Molecular Foundations"
                        lectures={foundationalLectures}
                        startIndex={0}
                      />
                    </TabsContent>

                    <TabsContent value="business" className="mt-0">
                      <CategorySection
                        title="Business & Practice Management"
                        lectures={businessLectures}
                        startIndex={0}
                      />
                    </TabsContent>
                  </Tabs>
                </div>
              </TabsContent>

              {/* Core Curriculum Section */}
              <TabsContent value="core-curriculum" className="mt-0">
                <div className="py-12">
                  {/* Welcome message for Core Curriculum */}
                  <div className="mb-10 bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 rounded-xl p-8 border border-indigo-100/50 shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">
                          Essential Learning Path
                        </h2>
                        <p className="text-sm text-gray-600 leading-relaxed mb-4">
                          This curated collection provides the foundational knowledge for longevity medicine practice. 
                          Follow the sequence to build from core concepts to advanced clinical applications.
                        </p>
                        <div className="flex flex-wrap items-center gap-6 text-xs">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>
                            <span className="text-gray-700 font-medium">{coreCurriculumLectures.length} Core Lectures</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                            <span className="text-gray-700 font-medium">Sequential Learning</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                            <span className="text-gray-700 font-medium">Clinical Focus</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <CategorySection
                    title="Essential Learning Path"
                    lectures={coreCurriculumLectures}
                    startIndex={0}
                  />
                </div>
              </TabsContent>

              {/* Tools Section */}
              <TabsContent value="tools" className="mt-0">
                <div className="py-12">
                  <div className="mb-6">
                    <h2 className="text-xl font-medium text-gray-900">Clinical Tools</h2>
                    <p className="text-xs text-gray-500 mt-1">
                      Interactive tools to support your longevity medicine practice
                    </p>
                  </div>
                  
                  <div className="space-y-6">
                    {/* Lab Report Generator Tool */}
                    <div className="py-4">
                      <div className="flex items-start gap-6 mb-6">
                        {/* Index number */}
                        <div className="flex-shrink-0 w-8 pt-0.5">
                          <span className="text-xs text-gray-400 font-mono">01</span>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-grow">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-grow">
                              <h3 className="text-base font-medium text-gray-900 mb-1">
                                Lab Report Generator
                              </h3>
                              <p className="text-sm text-gray-600 leading-relaxed mb-2 max-w-2xl">
                                Generate comprehensive laboratory reports using the SHIFT framework. Input patient test results and receive detailed interpretations with clinical recommendations.
                              </p>
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span className="uppercase tracking-wider">Clinical Tool</span>
                                <Circle className="w-1 h-1 fill-current" />
                                <span>SHIFT Framework</span>
                                <Circle className="w-1 h-1 fill-current" />
                                <span>Lab Interpretation</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Embedded Form */}
                      <div className="ml-14 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                        <iframe 
                          src="https://app.vectorshift.ai/forms/embedded/6835360dcf6faf7fa529815a" 
                          width="100%" 
                          height="900px" 
                          allow="clipboard-read; clipboard-write; microphone"
                          className="border-0"
                          title="Lab Report Generator"
                        />
                      </div>
                      
                      {/* Subtle separator */}
                      <div className="mt-6 ml-14">
                        <Separator className="bg-gray-100" />
                      </div>
                    </div>
                    
                    {/* Clinical Assessment Tool */}
                    <div className="py-4">
                      <div className="flex items-start gap-6 mb-6">
                        {/* Index number */}
                        <div className="flex-shrink-0 w-8 pt-0.5">
                          <span className="text-xs text-gray-400 font-mono">02</span>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-grow">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-grow">
                              <h3 className="text-base font-medium text-gray-900 mb-1">
                                Clinical Assessment Tool
                              </h3>
                              <p className="text-sm text-gray-600 leading-relaxed mb-2 max-w-2xl">
                                Advanced clinical assessment and analysis tool for comprehensive patient evaluation and treatment planning in longevity medicine.
                              </p>
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span className="uppercase tracking-wider">Clinical Tool</span>
                                <Circle className="w-1 h-1 fill-current" />
                                <span>Patient Assessment</span>
                                <Circle className="w-1 h-1 fill-current" />
                                <span>Treatment Planning</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Embedded Form */}
                      <div className="ml-14 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                        <iframe 
                          src="https://app.vectorshift.ai/forms/embedded/684f07a466d4e37c5d48d4bd" 
                          width="1000px" 
                          height="900px" 
                          allow="clipboard-read; clipboard-write; microphone"
                          className="border-0"
                          title="Clinical Assessment Tool"
                        />
                      </div>
                      
                      {/* Subtle separator */}
                      <div className="mt-6 ml-14">
                        <Separator className="bg-gray-100" />
                      </div>
                    </div>
                    
                    {/* Placeholder for future tools */}
                    <div className="bg-gray-50 rounded-lg p-6 border border-dashed border-gray-200">
                      <div className="text-center">
                        <h4 className="text-sm font-medium text-gray-700 mb-1">More Tools Coming Soon</h4>
                        <p className="text-xs text-gray-500">
                          Additional clinical tools and calculators will be added to support your practice
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* All Lectures Compact View */}
              <TabsContent value="all" className="mt-0">
                <div className="py-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-1">
                    {lectures.map(lecture => (
                      <CompactLectureItem key={lecture.id} lecture={lecture} />
                    ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        {/* Subtle CTA Section */}
        <section className="border-t border-gray-100 py-12 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-900 font-medium">
                  Ready to advance your practice?
                </p>
                <p className="text-sm text-gray-600 mt-0.5">
                  Join our mentorship program for full access.
                </p>
              </div>
              <Link href="/mentorship">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white"
                >
                  Learn more
                  <ArrowRight className="ml-2 h-3.5 w-3.5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MentorshipContent;